import { useEffect, useMemo, useRef, useState } from 'react';
import { useEmployeeStore } from '../store/employeeStore';
import { Avatar, AvatarImage, AvatarFallback } from './Avatar';
import { Input } from './Input';
import { cn } from '../lib/utils';
import { Check, Loader2, Search, X } from 'lucide-react';

export interface PickedPerson {
  userId: string;
  name: string;
  email: string;
  avatar?: string;
}

/**
 * Searchable person picker. Lists employees (name/email/avatar) and resolves the
 * selection to the person's auth.users id (people.employees.user_id), which is
 * what vault membership and grants key off. Employees without a linked account
 * are shown disabled, since they cannot be a vault member.
 */
export function PeoplePicker({
  value,
  onChange,
  placeholder = 'Search people by name or email…',
  excludeUserIds = [],
}: {
  value?: string;
  onChange: (person: PickedPerson | null) => void;
  placeholder?: string;
  excludeUserIds?: string[];
}) {
  const fetchAllEmployees = useEmployeeStore((s) => s.fetchAllEmployees);
  const [people, setPeople] = useState<PickedPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAllEmployees()
      .then((rows: any[]) => {
        if (cancelled) return;
        const mapped: PickedPerson[] = rows
          .map((r) => ({
            userId: r.user_id || r.userId || '',
            name: r.displayName || `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email,
            email: r.email || '',
            avatar: r.avatar,
          }))
          .filter((p) => p.userId); // only people with a linked account can be members
        setPeople(mapped);
      })
      .catch(() => setPeople([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [fetchAllEmployees]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = useMemo(() => people.find((p) => p.userId === value) || null, [people, value]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const excluded = new Set(excludeUserIds);
    return people
      .filter((p) => !excluded.has(p.userId))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
      .slice(0, 50);
  }, [people, query, excludeUserIds]);

  const initials = (name: string) =>
    name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'U';

  // Selected state: show a chip with a clear button.
  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="size-7 shrink-0">
            <AvatarImage src={selected.avatar} alt={selected.name} />
            <AvatarFallback className="text-[10px] bg-primary text-white">{initials(selected.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">{selected.name}</div>
            <div className="truncate text-xs text-muted-foreground">{selected.email}</div>
          </div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="shrink-0 text-muted-foreground hover:text-foreground" aria-label="Clear selection">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading people…
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">No matching people.</div>
          ) : (
            results.map((p) => (
              <button
                key={p.userId}
                type="button"
                onClick={() => { onChange(p); setOpen(false); setQuery(''); }}
                className={cn('flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-secondary/60')}
              >
                <Avatar className="size-7 shrink-0">
                  <AvatarImage src={p.avatar} alt={p.name} />
                  <AvatarFallback className="text-[10px] bg-primary text-white">{initials(p.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{p.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{p.email}</div>
                </div>
                {value === p.userId && <Check className="size-4 text-primary" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
